import { useEffect, useState } from 'react'
import { shuffledOrder } from './shuffledOrder'
import './RotatingPlaceholder.scss'

/** So lange blendet der alte Text aus, bevor der neue kommt — gleich der Dauer in `RotatingPlaceholder.scss`. */
const FADE_MS = 350

export interface RotatingPlaceholderProps {
  /** Die Beispiele, die abwechselnd stehen. Bei nur einem steht es still, es läuft kein Takt. */
  examples: string[]
  /** Abstand zwischen zwei Wechseln in ms. */
  interval?: number
  /** Hält den Wechsel an (inaktiver Tab) — der Text bleibt stehen. */
  paused?: boolean
  /** Lage und Schrift bestimmt der Einbauort (hinter einem durchsichtigen Feld, gleiche Schrift wie das Feld). */
  className?: string
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

/** Ein Durchgang: die Reihenfolge der Beispiele und wo er steht. */
interface Cycle {
  order: number[]
  position: number
}

/**
 * Die Systemeinstellung „Bewegung reduzieren“, auch wenn sie bei laufender App umgestellt wird. Bewusst nicht
 * `useReducedMotion` aus framer-motion: Das liest sie nur beim Einhängen — das langlebige Schnelleingabe-Fenster
 * wechselte nach dem Umstellen weiter, ohne Überblendung (das CSS folgt sofort).
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(REDUCED_MOTION).matches === true)
  useEffect(() => {
    const media = window.matchMedia?.(REDUCED_MOTION)
    if (!media) return
    const update = () => setReduced(media.matches)
    media.addEventListener('change', update)
    update()
    return () => media.removeEventListener('change', update)
  }, [])
  return reduced
}

function useDocumentHidden(): boolean {
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden)
  useEffect(() => {
    const update = () => setHidden(document.hidden)
    document.addEventListener('visibilitychange', update)
    update()
    return () => document.removeEventListener('visibilitychange', update)
  }, [])
  return hidden
}

/**
 * **Beispiele, die sich abwechseln — als Platzhalter eines Felds** (#258, #239). Ein nativer `placeholder`
 * lässt sich nicht überblenden; dieser Text liegt deshalb als Überlagerung HINTER dem durchsichtigen Feld.
 * Er ist `aria-hidden`: Das Feld braucht ein eigenes `aria-label`, und ein Screenreader soll nicht alle paar
 * Sekunden ein neues Beispiel vorlesen.
 *
 * Ein Durchgang zeigt jedes Beispiel einmal in zufälliger Reihenfolge, dann wird neu gemischt. **Der Takt ruht**,
 * solange `paused` gilt, das Fenster versteckt ist oder bei reduzierter Bewegung (dann steht ein zufälliges
 * Beispiel still — ein Text, der sich selbst ändert, ist dort Unruhe, auch ohne Überblendung). Der Zustand liegt
 * hier und nicht beim Feld: Sonst rendert die ganze Zeile alle paar Sekunden neu.
 *
 * Eingebaut wird er nur, solange das Feld leer ist — mit dem ersten Zeichen verschwindet er samt Takt.
 *
 * @example
 * <div style={{ position: 'relative' }}>
 *   {!value && <RotatingPlaceholder examples={['Steuer sortieren', 'Joggen jeden Montag 30min']} />}
 *   <input aria-label="Aufgabentitel" value={value} onChange={…} />
 * </div>
 */
export function RotatingPlaceholder({ examples, interval = 4000, paused = false, className }: RotatingPlaceholderProps) {
  const reduced = usePrefersReducedMotion()
  const hidden = useDocumentHidden()
  const [cycle, setCycle] = useState<Cycle>(() => ({ order: shuffledOrder(examples.length, null), position: 0 }))
  const [fading, setFading] = useState(false)

  // Ändert sich die Anzahl der Beispiele (sie kommen später, andere Sprache), wird neu gemischt — sonst stünde die
  // Reihenfolge von früher da: nur Beispiel 0, oder Indizes, die es nicht mehr gibt. Während des Renderns gesetzt,
  // React rechnet den Durchgang gleich noch einmal; ohne Zwischenbild mit dem alten Stand.
  if (cycle.order.length !== examples.length) setCycle({ order: shuffledOrder(examples.length, null), position: 0 })

  const running = !paused && !hidden && !reduced && examples.length > 1

  useEffect(() => {
    if (!running) return
    let swap: ReturnType<typeof setTimeout> | undefined
    const tick = setInterval(() => {
      setFading(true)
      swap = setTimeout(() => {
        setCycle(({ order, position }) => position + 1 < order.length
          ? { order, position: position + 1 }
          : { order: shuffledOrder(order.length, order[position] ?? null), position: 0 })
        setFading(false)
      }, FADE_MS)
    }, interval)
    return () => {
      clearInterval(tick)
      clearTimeout(swap)
      // Mitten im Ausblenden angehalten: sonst bliebe der Text unsichtbar stehen.
      setFading(false)
    }
  }, [running, interval])

  // Bis der neue Durchgang steht (siehe oben), reichen die Grenzen hier: kein Zugriff außerhalb der Beispiele.
  const index = cycle.order[cycle.position % Math.max(cycle.order.length, 1)] ?? 0
  const text = examples.length > 0 ? examples[index % examples.length] : ''

  return (
    <span aria-hidden="true" className={['rotating-placeholder', fading && 'rotating-placeholder--fading', className].filter(Boolean).join(' ')}>
      {text}
    </span>
  )
}
